import { Injectable } from '@angular/core'
import { BehaviorSubject } from 'rxjs'
import { WidgetInterface } from '@modules/widgets-module/interfaces/widget.interface'
import { Gridster, GridsterItemConfig } from 'angular-gridster2'

@Injectable({
	providedIn: 'root',
})
export class GridsterService {
	protected readonly math: Math = Math

	public gridster$: BehaviorSubject<Gridster | null> = new BehaviorSubject<Gridster | null>(null)
	public isDragAndResize$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)

	/**
	 * Проверяет, перекрываются ли два виджета по координатам на сетке.
	 * Виджеты считаются перекрывающимися, если они частично или полностью занимают одни и те же ячейки по осям X и Y.
	 * widgetA - Первый виджет для сравнения.
	 * widgetB - Второй виджет для сравнения.
	 * true — если виджеты перекрываются, иначе false.
	 */
	private _compareWidgetsCoordinates(widgetA: GridsterItemConfig, widgetB: GridsterItemConfig): boolean {
		const overlapsX: boolean = widgetA.x < widgetB.x + widgetB.cols && widgetA.x + widgetA.cols > widgetB.x
		const overlapsY: boolean = widgetA.y < widgetB.y + widgetB.rows && widgetA.y + widgetA.rows > widgetB.y

		return overlapsX && overlapsY
	}

	/**
	 * Проверяет, наложились ли какие-либо виджеты друг на друга после действия пользователя (перетаскивания/ресайз).
	 * Перебирает все виджеты в массиве и сравнивает каждый виджет с остальными через compareWidgetsCoordinates(widgetA, widgetB)
	 * Если хотя бы два виджета занимают одну и ту же область на сетке — возвращается true. (в dashboard true возвращает виджеты в исходные позиции)
	 * true — если обнаружено пересечение хотя бы двух виджетов, иначе false.
	 */
	public checkWidgetsOverlap(widgets: WidgetInterface[]): boolean {
		for (let i: number = 0; i < widgets.length; i++) {
			const widgetA: GridsterItemConfig | undefined = widgets[i]?.gridsterItem
			for (let j: number = i + 1; j < widgets.length; j++) {
				const widgetB: GridsterItemConfig | undefined = widgets[j]?.gridsterItem

				if (!widgetA || !widgetB) return false
				if (this._compareWidgetsCoordinates(widgetA, widgetB)) return true
			}
		}
		return false
	}

	public toggleDragAndResize(value: boolean): void {
		this.isDragAndResize$.next(value)
	}

	public setGridster(gridster: Gridster): void {
		this.gridster$.next(gridster)
	}

	public clearGridster(): void {
		this.gridster$.next(null)
	}

	/**
	 * Вычисляет максимально возможный размер (cols × rows) нового виджета, который может быть размещён на сетке.
	 * Метод начинает с дефолтных размеров виджета (defaultCols и defaultRows) и пытается найти свободное место на сетке
	 * с текущими размерами. Если размещение невозможно, он постепенно уменьшает размеры до минимально допустимых
	 * (minItemCols и minItemRows), пока не найдёт подходящую позицию.
	 * Возвращаем GridsterItem с максимально подходяющими размерами
	 * или null, если ни один вариант не подходит. (Выходит alert что нет места)
	 */
	public calcMaxWidgetSize(widget: WidgetInterface): GridsterItemConfig | null {
		const gridster: Gridster | null = this.gridster$.getValue()
		if (!gridster) return null

		const minCols: number = widget.gridsterItem.minItemCols ?? 0
		const minRows: number = widget.gridsterItem.minItemRows ?? 0
		let cols: number = widget.gridsterItem.cols ?? 0
		let rows: number = widget.gridsterItem.rows ?? 0

		if (!gridster?.getNextPossiblePosition) return null

		while (cols >= minCols && rows >= minRows) {
			const sizes: GridsterItemConfig = { ...widget.gridsterItem, cols, rows }
			const isWidgetFits: boolean = gridster.getNextPossiblePosition(sizes)
			if (isWidgetFits) return sizes

			// Если достигнут минимальный rows, уменьшается только cols.
			if (cols > minCols && rows === minRows) {
				cols -= 1

				// Если достигнут минимальный cols, уменьшается только rows.
			} else if (rows > minRows && cols === minCols) {
				rows -= 1

				// Если оба размера больше минимальных — уменьшаются оба.
			} else if (cols > minCols && rows > minRows) {
				cols -= 1
				rows -= 1
			} else {
				break
			}
		}
		return null
	}

	/**
	 * Вычисляет width/height одной ячейки
	 */
	public calcGridsterDots(): void {
		const grid: HTMLElement | undefined = this.gridster$.getValue()?.el
		if (!grid) return

		grid.style.setProperty('--col-width', `${grid.offsetWidth / 25}px`)
		grid.style.setProperty('--row-height', `${grid.offsetHeight / 25}px`)
	}
}
